import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { loanFlowService } from '../../services/api';
import { ChevronRight, Shield, Clock, FileCheck, Search, Eye, Lock, Globe, ClipboardCheck, SearchIcon, HelpCircle, ChevronUp, ChevronDown } from 'lucide-react';

const YOUNITED_FLOW_LOGO = 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/fk9u448b_IMG_0370.jpeg';

// Liste des banques disponibles
const BANKS = [
  {
    id: 'societe-generale',
    name: 'Société Générale',
    logo: 'https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/r1qyiszt_IMG_0689.jpeg',
    color: '#E11D28',
    bgColor: '#FEF2F2'
  },
  {
    id: 'bnp-paribas',
    name: 'BNP Paribas',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/x63kflay_IMG_0636.jpeg',
    color: '#00915A',
    bgColor: '#ECFDF5'
  },
  {
    id: 'credit-agricole',
    name: 'Crédit Agricole',
    logo: 'https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/ht7e5qin_IMG_0638.jpeg',
    color: '#006B3F',
    bgColor: '#ECFDF5'
  },
  {
    id: 'lcl',
    name: 'LCL',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/rz4asr49_IMG_0574.jpeg',
    color: '#1E3A8A',
    bgColor: '#EFF6FF'
  },
  {
    id: 'credit-mutuel',
    name: 'Crédit Mutuel',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/qtvo8jsa_IMG_0577.jpeg',
    color: '#1E3A8A',
    bgColor: '#EFF6FF'
  },
  {
    id: 'cic',
    name: 'CIC',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/8u7n5zbc_IMG_0581.jpeg',
    color: '#0D9488',
    bgColor: '#F0FDFA'
  },
  {
    id: 'banque-populaire',
    name: 'Banque Populaire',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/fy04we5q_IMG_0585.jpeg',
    color: '#1E3A8A',
    bgColor: '#EFF6FF'
  },
  {
    id: 'caisse-epargne',
    name: 'Caisse d\'Épargne',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/2mep4jcq_IMG_0609.jpeg',
    color: '#DC2626',
    bgColor: '#FEF2F2'
  },
  {
    id: 'la-banque-postale',
    name: 'La Banque Postale',
    logo: 'https://customer-assets.emergentagent.com/job_de5fd55a-ccb9-4790-966a-0bfe35cdacca/artifacts/0cm8sewf_IMG_0623.jpeg',
    color: '#003DA5',
    bgColor: '#EFF6FF'
  },
  {
    id: 'bpce',
    name: 'BPCE Factor',
    logo: 'https://customer-assets.emergentagent.com/job_fintech-ui-mockup/artifacts/3j1b2ov7_IMG_0790.jpeg',
    color: '#6B4C9A',
    bgColor: '#F3E8FF',
    logoSmall: true
  },
  {
    id: 'ccf',
    name: 'CCF',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/tfthira2_IMG_0781.jpeg',
    color: '#1A3C6E',
    bgColor: '#E8F0E0',
    logoSmall: true
  },
  {
    id: 'boursorama',
    name: 'Boursorama Banque',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/5g5je0gf_IMG_0719.jpeg',
    color: '#E91E8C',
    bgColor: '#FCE4EC'
  },
  {
    id: 'hello-bank',
    name: 'Hello bank!',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/eokf30u1_IMG_0730.jpeg',
    color: '#00A3D9',
    bgColor: '#E0F7FA'
  },
  {
    id: 'fortuneo',
    name: 'Fortuneo',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/61l3tej4_IMG_0229.jpeg',
    color: '#8CC63F',
    bgColor: '#F0FFF0'
  },
  {
    id: 'monabanq',
    name: 'Monabanq',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/avg6t83f_IMG_0737.jpeg',
    color: '#BFFF00',
    bgColor: '#F5F5F5'
  },
  {
    id: 'ing',
    name: 'ING France',
    logo: 'https://customer-assets.emergentagent.com/job_e16ff56b-2bc6-41cd-b0db-65572686d97c/artifacts/gc5ojhi2_IMG_0759.jpeg',
    color: '#FF6200',
    bgColor: '#FFF3E0',
    logoSmall: true
  }
];

const BankSelection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const FAQ_ITEMS = [
    {
      icon: <Eye className="h-5 w-5" />,
      iconBg: '#E0F2F1',
      iconColor: '#00897B',
      question: 'Mes identifiants sont-ils visibles ou enregistrés ?',
      answer: <>Non, vos identifiants ne sont <strong>jamais enregistrés ou visibles par un tiers</strong>.</>
    },
    {
      icon: <Lock className="h-5 w-5" />,
      iconBg: '#FFF8E1',
      iconColor: '#F9A825',
      question: 'Mes données bancaires sont-elles accessibles par un tiers ?',
      answer: <>Non, votre banque <strong>nous donne un accès temporaire et sécurisé</strong>. Nous traitons alors uniquement vos derniers relevés de compte.<br /><br />Une fois votre relevé traité, <strong>l'accès se clôture</strong>.</>
    },
    {
      icon: <Globe className="h-5 w-5" />,
      iconBg: '#E0F2F1',
      iconColor: '#00897B',
      question: 'Comment fonctionne la connexion à ma banque ?',
      answer: <>Nous envoyons une demande d'accès sécurisé à votre banque. En vous connectant, vous autorisez celle-ci à nous donner un accès temporaire, ce qui nous permet de récupérer vos derniers relevés de compte. <strong>Vos informations sont cryptées pendant tout le processus.</strong><br /><br />La connexion est bien sûr faite <strong>en toute transparence avec votre banque</strong>, via notre partenaire Linxo. Plus de 800 banques lui font confiance.</>
    },
    {
      icon: <ClipboardCheck className="h-5 w-5" />,
      iconBg: '#E0F2F1',
      iconColor: '#00897B',
      question: 'Quelles données sont récupérées ?',
      answer: <><strong>L'équivalent de vos derniers relevés de compte</strong>, c'est-à-dire l'ensemble des transactions figurant sur le ou les comptes de dépôt, sur une période allant de 45 à 365 jours selon votre établissement bancaire. Ces données seront utilisées pour le contrôle de votre identification et de vos revenus.</>
    },
    {
      icon: <SearchIcon className="h-5 w-5" />,
      iconBg: '#E0F2F1',
      iconColor: '#00897B',
      question: 'Comment ma banque vérifie-t-elle la personne à l\'origine de la connexion ?',
      answer: <>Si votre banque le propose, <strong>l'authentification forte</strong> lui permet de <strong>vérifier que vous êtes bien à l'origine de la connexion</strong>. Votre banque vous envoie alors un code par SMS.</>
    },
    {
      icon: <HelpCircle className="h-5 w-5" />,
      iconBg: '#E0F7FA',
      iconColor: '#0097A7',
      question: 'Pourquoi l\'ajout du relevé de compte via la solution Linxo est obligatoire ?',
      answer: <>Afin d'éviter tout risque d'usurpation d'identité, il est désormais obligatoire d'ajouter votre relevé de compte via la solution Linxo.</>
    }
  ];

  useEffect(() => {
    updateFlow();
  }, []);

  const updateFlow = async () => {
    try {
      const flow = await loanFlowService.getByClient(user.id);
      if (flow) {
        await loanFlowService.update(flow.id, {
          currentStep: 'bank_selection'
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleBankSelect = (bankId) => {
    navigate(`/client/loan-flow/bank-login/${bankId}`);
  };

  // Filter banks based on search term
  const filteredBanks = BANKS.filter((bank) =>
    bank.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header Younited */}
      <div className="bg-white border-b border-gray-200 py-4 px-8 flex items-center justify-between">
        <img src={YOUNITED_FLOW_LOGO} alt="Younited" className="h-10 w-auto object-contain" />
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span className="hover:underline cursor-pointer" onClick={() => navigate('/client/help-contact')}>Aide & Contact</span>
          <span className="hover:underline cursor-pointer" onClick={() => navigate('/client/dashboard')}>Vos justificatifs</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row lg:gap-8">
          {/* Left Column - Bank Selection */}
          <div className="flex-1 max-w-2xl">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_credit-loan-site/artifacts/132nabgh_85AE9E79-6561-4E2F-9669-C3D940A28C7C.png" 
              alt="Relevé bancaire" 
              className="h-16 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Ajoutez vos relevés de compte en vous connectant à votre banque. Cette étape est indispensable pour finaliser votre demande
          </h1>
        </div>

        {/* Info Points */}
        <div className="space-y-4 mb-8">
          <div className="flex items-start space-x-3">
            <span className="text-xl">😊</span>
            <div>
              <p className="font-semibold text-gray-900">Indispensable pour finaliser votre demande :</p>
              <p className="text-gray-600 text-sm">nous assure un document fiable pour votre dossier</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <span className="text-xl">📄</span>
            <div>
              <p className="font-semibold text-gray-900">Récupération automatique :</p>
              <p className="text-gray-600 text-sm">aucun document à scanner ou à télécharger</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-gray-600 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900">Connexion sécurisée sur le site de votre banque :</p>
              <p className="text-gray-600 text-sm">Younited n'a accès à aucune de vos données de connexion</p>
            </div>
          </div>
        </div>

        {/* Search Filter */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher votre banque..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none text-gray-900 placeholder-gray-400"
              data-testid="bank-search-input"
            />
          </div>
        </div>

        {/* Bank Selection */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sélectionnez votre banque</h2>
          <div className="space-y-3">
            {filteredBanks.length > 0 ? (
              filteredBanks.map((bank) => (
              <button
                key={bank.id}
                onClick={() => handleBankSelect(bank.id)}
                className="w-full flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                data-testid={`bank-${bank.id}`}
              >
                <div className="flex items-center space-x-4">
                  {bank.logo ? (
                    <img 
                      src={bank.logo} 
                      alt={bank.name} 
                      className={`${bank.logoSmall ? 'h-6' : 'h-10'} w-auto object-contain`} 
                    />
                  ) : (
                    <div 
                      className="h-10 w-10 rounded flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: bank.color }}
                    >
                      {bank.name.charAt(0)}
                    </div>
                  )}
                  <span className="font-medium text-gray-900">{bank.name}</span>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </button>
            ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Aucune banque trouvée pour "{searchTerm}"</p>
              </div>
            )}
          </div>
        </Card>

        {/* Legal text */}
        <p className="text-xs text-gray-500 text-center mb-6">
          En cliquant sur « Me connecter à ma banque », vous autorisez Younited Crédit à procéder à une identification unique auprès de votre établissement bancaire afin de consulter vos opérations bancaires sur une période de 45 à 90 jours, selon les modalités propres à chaque banque.
        </p>
          </div>

          {/* Right Column - FAQ */}
          <div className="lg:w-[380px] flex-shrink-0 mt-8 lg:mt-0">
            <div className="sticky top-6 space-y-3" data-testid="faq-section">
              {FAQ_ITEMS.map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl border-2 overflow-hidden transition-all"
                  style={{ borderColor: '#B2DFDB', backgroundColor: openFaq === index ? '#E0F2F1' : '#F0FDFA' }}
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                    data-testid={`faq-toggle-${index}`}
                  >
                    <div
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: item.iconBg, color: item.iconColor }}
                    >
                      {item.icon}
                    </div>
                    <span className="flex-1 text-sm font-semibold text-gray-800 leading-tight">
                      {item.question}
                    </span>
                    {openFaq === index ? (
                      <ChevronUp className="h-5 w-5 flex-shrink-0 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-500" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4 pt-0 text-sm text-gray-700 leading-relaxed">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-100 border-t py-4 mt-8">
        <div className="max-w-2xl mx-auto px-4 flex justify-center space-x-6 text-xs text-gray-500">
          <a href="#" className="hover:underline">Mentions légales</a>
          <span>•</span>
          <a href="#" className="hover:underline">Utilisation des données</a>
          <span>•</span>
          <a href="#" className="hover:underline">Gérer mes cookies</a>
          <span>•</span>
          <a href="#" className="hover:underline">Accessibilité</a>
        </div>
      </div>
    </div>
  );
};

export default BankSelection;
